import { Dtos, type SuretyAccountId } from "@inrev/common";
import { isApiError } from "../../../utils/error";
import { useRequest } from "../../../utils/request";
import { proConsQueryKey } from "../decisioning.keys";
import { mergeById } from "../optimistic";
import { reorderBucket } from "../rules";
import { type ProCon } from "../types";
import { useOptimisticListMutation } from "../useOptimisticListMutation";

export type ReorderProConsInput = Dtos.Decisioning.ProCon.Reorder.Request;

export const useReorderProCons = (accountId: SuretyAccountId) => {
	const { put } = useRequest();

	const mutation = useOptimisticListMutation<ProCon, ReorderProConsInput, ProCon[]>({
		queryKey: proConsQueryKey(accountId),
		request: async (request) =>
			Dtos.Decisioning.ProCon.Get.Response.schema
				.array()
				.parse(
					await put<unknown>(`/v2/admin/surety/accounts/${accountId}/pro-cons/order`, request),
				),
		apply: (items, request) => reorderBucket(items, request.kind, request.importance, request.ids),
		affectedIds: (request) => request.ids,
		failureMessage: (_request, error) =>
			isApiError(error) && (error.statusCode === 400 || error.statusCode === 409)
				? "The list changed elsewhere, so it was refreshed. Try the move again."
				: "Couldn't save the new order.",
		reconcile: (items, proCons) => mergeById(items, proCons),
	});

	return {
		reorderProCons: mutation.mutate,
	};
};
